import * as React from "react";

/**
 * A value that settles `delay` ms after it stops changing.
 *
 * For search boxes whose value becomes a request. Without it every keystroke is
 * a fetch, and because the responses can land out of order the list settles on
 * whichever query the server happened to finish last - which is not necessarily
 * the one still in the box.
 *
 * Lives here rather than beside the one page that first needed it: it was
 * private to `Team.tsx`, which is part of why the other list pages either had no
 * debounce or no search at all.
 */
export function useDebounced<T>(value: T, delay: number): T {
  const [settled, setSettled] = React.useState(value);

  React.useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return settled;
}

/**
 * The interval every list in the console debounces its search box by.
 *
 * 300ms rather than 250. It is the number `kSearchDebounce` in
 * `mobile-app/lib/core/util/debounce.dart` has always used and claimed in its
 * own comment to be matching, so the two clients were 50ms apart while both
 * files said they agreed. This is the half that was wrong.
 *
 * It is also the right number on its own terms: 250 fires inside the pause a
 * touch typist leaves between words, so "north yard" was three requests where
 * the reader made one search. Past roughly 350 the list starts feeling like it
 * is lagging the box.
 */
export const SEARCH_DEBOUNCE_MS = 300;
